import { FC, useCallback, useEffect, useState } from 'react'
import cx from 'classnames'
import Image from 'next/image'
import { useTranslation } from 'next-i18next'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import * as Tooltip from '@radix-ui/react-tooltip'
import * as Dialog from '@radix-ui/react-dialog'

import { PrimaryButton } from 'ui'
import { LoadingSpinner } from 'ui'

import {
  ChevronIcon,
  ExternalLinkIcon as ShareIcon,
  CloseLgIcon,
  LinkIcon,
} from 'icons'

import { wrap } from 'utils'

import { PhotoResolutions } from '../services/getListings'

interface IGeneralArrowButtonProps {
  label: string
  className?: string
  iconClassName: string
  handleClick: () => void
}

const ArrowButton = ({
  className,
  label,
  iconClassName,
  handleClick,
}: IGeneralArrowButtonProps) => {
  return (
    <button
      className={cx(className, 'pointer-events-auto rounded-md bg-black')}
      onClick={handleClick}
    >
      <span className="sr-only">{label}</span>
      <ChevronIcon
        className={cx(
          'h-40 w-40 stroke-white [vector-effect:non-scaling-stroke] md:h-80 md:w-80',
          iconClassName
        )}
      />
    </button>
  )
}

interface ISpecificArrowButtonProps {
  className?: string
  handleClick: () => void
}

const PreviousButton = ({
  handleClick,
  className,
}: ISpecificArrowButtonProps) => {
  const { t } = useTranslation('common')
  return (
    <ArrowButton
      className={className}
      label={t('ui.previous')}
      handleClick={handleClick}
      iconClassName="rotate-90"
    />
  )
}

const NextButton = ({ handleClick, className }: ISpecificArrowButtonProps) => {
  const { t } = useTranslation('common')
  return (
    <ArrowButton
      className={className}
      label={t('ui.next')}
      handleClick={handleClick}
      iconClassName="-rotate-90"
    />
  )
}

const ShareButton = () => {
  const { t } = useTranslation(['common'])
  const [showToast, setShowToast] = useState(false)

  const onCopy = () => {
    setShowToast(true)
    setTimeout(() => {
      setShowToast(false)
    }, 3000)
  }

  return (
    <div className="relative z-20">
      <div
        aria-live="polite"
        className={cx(
          'absolute top-full left-1/2 z-10 mt-20 -translate-x-1/2',
          'flex min-w-[235px] items-center justify-between',
          'rounded-sm bg-teal-3 p-16 transition-opacity',
          {
            'opacity-0': !showToast,
            'pointer-events-none': !showToast,
          }
        )}
      >
        {showToast ? (
          <>
            <LinkIcon className="stroke-white rect-18" />
            <p className="ml-10 text-14 font-semibold text-white">
              {t('ui.link-copied')}
            </p>
          </>
        ) : null}
      </div>
      <Tooltip.Root delayDuration={500}>
        <Tooltip.Trigger asChild>
          <div>
            <CopyToClipboard
              text={typeof window !== 'undefined' ? window.location.href : ''}
              onCopy={onCopy}
            >
              <PrimaryButton icon={ShareIcon}>{t('ui.share')}</PrimaryButton>
            </CopyToClipboard>
          </div>
        </Tooltip.Trigger>
        <Tooltip.Content
          alignOffset={-10}
          sideOffset={5}
          side="top"
          className={cx('rounded-sm bg-black px-16 py-8 shadow-sm')}
        >
          <p className="text-14 font-semibold text-white">
            {t('ui.copy-link')}
          </p>
          <Tooltip.Arrow className="fill-black" offset={10} />
        </Tooltip.Content>
      </Tooltip.Root>
    </div>
  )
}

export interface ICarouselModalProps {
  isOpen: boolean
  images: PhotoResolutions[]
  initialIndex?: number
  handleClose: () => void
  srHeading: string
}

const CarouselModal: FC<ICarouselModalProps> = ({
  isOpen,
  images = [],
  initialIndex = 0,
  handleClose,
  srHeading,
}) => {
  const { t } = useTranslation('common')
  const [currentImageIndex, setCurrentImageIndex] = useState(initialIndex)
  const showArrows = images.length > 1

  const handleImageChange = useCallback(
    (dir: 'prev' | 'next') => {
      const newIndex = wrap(
        dir === 'prev' ? currentImageIndex - 1 : currentImageIndex + 1,
        images.length
      )
      setCurrentImageIndex(newIndex)
    },
    [currentImageIndex, images]
  )

  useEffect(() => {
    setCurrentImageIndex(initialIndex)
  }, [initialIndex, isOpen, images])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleKeydown = (e: KeyboardEvent) => {
      if (e.code === 'ArrowLeft') {
        handleImageChange('prev')
        return
      }

      if (e.code === 'ArrowRight') {
        handleImageChange('next')
      }

      return
    }

    document.addEventListener('keydown', handleKeydown)

    return () => document.removeEventListener('keydown', handleKeydown)
  }, [currentImageIndex, handleImageChange, isOpen])

  return (
    <Dialog.Root open={isOpen} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-modal bg-black/80">
          <Dialog.Title asChild>
            <span className="sr-only">{srHeading}</span>
          </Dialog.Title>
          <Dialog.Content className="absolute inset-0 flex flex-col">
            <div className="border-b-2 border-white/30 bg-black px-16">
              <div className="mx-auto flex max-w-1200 justify-between">
                <div className="flex flex-col justify-center border-b-2 border-teal-3 text-white">
                  Photos
                </div>
                <div className="flex flex-row-reverse space-x-10 space-x-reverse py-10">
                  <PrimaryButton icon={CloseLgIcon} onClick={handleClose}>
                    {t('ui.close')}
                  </PrimaryButton>
                  <ShareButton />
                </div>
              </div>
            </div>

            {images.length ? (
              <div className="relative z-10 mx-auto flex w-full max-w-1440 grow flex-col justify-center p-16 md:py-32">
                <div className="absolute inset-0 -z-10" onClick={handleClose} />
                {showArrows ? (
                  <div className="flex justify-center space-x-20 md:hidden">
                    <PreviousButton
                      handleClick={() => handleImageChange('prev')}
                    />
                    <NextButton handleClick={() => handleImageChange('next')} />
                  </div>
                ) : null}
                <div className="pointer-events-none flex h-full justify-center md:items-center md:justify-between md:space-x-40 md-d:mt-20">
                  {showArrows ? (
                    <PreviousButton
                      className="pointer-events-auto md-d:hidden"
                      handleClick={() => handleImageChange('prev')}
                    />
                  ) : null}
                  <div className="pointer-events-auto relative h-full grow overflow-hidden rounded bg-black">
                    <LoadingSpinner light />
                    <LoadImage img={images[currentImageIndex - 1]} />

                    <Image
                      src={images[currentImageIndex].high}
                      layout="fill"
                      quality={100}
                      objectFit="contain"
                      alt=""
                    />

                    <LoadImage img={images[currentImageIndex + 1]} />
                  </div>
                  {showArrows ? (
                    <NextButton
                      className="pointer-events-auto md-d:hidden"
                      handleClick={() => handleImageChange('next')}
                    />
                  ) : null}
                </div>
              </div>
            ) : null}
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const LoadImage: FC<{ img: PhotoResolutions }> = ({ img }) => {
  if (!img) {
    return null
  }

  return (
    <div className="pointer-events-none opacity-0" aria-hidden="true">
      <Image
        src={img.high}
        layout="fill"
        quality={100}
        objectFit="contain"
        alt=""
      />
    </div>
  )
}

export default CarouselModal
